import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { ArrowRight, CheckCircle2, ChevronDown, Mail } from 'lucide-react';

import { useInvisibleRecaptcha } from '../../hooks/useInvisibleRecaptcha';
import {
  LANDING_SIGNUP_URL,
  PRIVACY_POLICY_URL,
  captchaSiteKey,
  isInAppEarlyAccessEnabled,
  joinWaitlist,
} from '../../services/earlyAccess';
import { AuthButton } from './AuthButton';
import { AuthInput } from './AuthInput';

const PROFESSIONS = [
  { value: 'designer', label: "I'm a lighting designer" },
  { value: 'architect', label: "I'm an architect or urban planner" },
  { value: 'engineer', label: "I'm a lighting systems engineer" },
  { value: 'manager', label: "I'm a municipal manager" },
  { value: 'manufacturer', label: "I'm a lighting manufacturer" },
  { value: 'curious-product', label: "I'm just curious about the product" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'intro' | 'form' | 'done';

interface EarlyAccessCardProps {
  /** Set when Google sign-in came back for an email that has no account. */
  notInvited?: boolean;
  /** Opens the form straight away, e.g. from /register or `?subscribe=1`. */
  initialOpen?: boolean;
}

export const EarlyAccessCard = ({ notInvited = false, initialOpen = false }: EarlyAccessCardProps) => {
  const inApp = isInAppEarlyAccessEnabled();
  const [step, setStep] = useState<Step>(inApp && initialOpen ? 'form' : 'intro');
  const [sentTo, setSentTo] = useState('');
  const [alreadyListed, setAlreadyListed] = useState(false);

  const title =
    step === 'done'
      ? alreadyListed
        ? "You're already on the list"
        : "You're on the list"
      : notInvited
        ? "This account isn't invited yet"
        : step === 'form'
          ? 'Request an invitation'
          : 'New to TAMAN?';
  const Icon = step === 'done' ? CheckCircle2 : Mail;

  return (
    <section
      aria-labelledby="early-access-title"
      className={`rounded-xl border p-3.5 xs:p-4 bg-gradient-to-b from-green01/[0.09] to-green01/[0.02] ${
        notInvited ? 'border-green01/60' : 'border-green01/25'
      }`}
    >
      {/* Tighter below 375px so "New to TAMAN?" and the pill still fit on one line at 320px. */}
      <div className="flex items-center gap-2.5 xs:gap-3">
        <Icon size={20} className="text-green01 shrink-0" aria-hidden="true" />
        <h2 id="early-access-title" className="flex-1 min-w-0 text-white text-base font-semibold leading-snug">
          {title}
        </h2>
        {step === 'intro' && !notInvited && (
          <span className="shrink-0 whitespace-nowrap rounded-full border border-white/25 px-2.5 py-0.5 text-[10px] font-medium tracking-[0.1em] xs:tracking-[0.14em] text-white/70 uppercase">
            Invite only
          </span>
        )}
      </div>

      {step === 'intro' && (
        <div className="mt-2.5 flex flex-col gap-3.5">
          <div className="flex flex-col gap-1">
            <p className="text-[15px] text-white font-medium leading-snug">
              TAMAN is in <span className="text-green01">private beta</span>
              {notInvited
                ? ", so this Google account can't sign in yet."
                : '. A selected group is testing it right now.'}
            </p>
            <p className="text-sm text-white/70 leading-snug">
              Subscribe for early access and we&apos;ll email your invitation when it&apos;s your turn.
            </p>
          </div>
          {inApp ? (
            <AuthButton onClick={() => setStep('form')}>
              Subscribe for early access <ArrowRight size={16} aria-hidden="true" />
            </AuthButton>
          ) : (
            <a
              href={LANDING_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-green01 text-black hover:bg-green02 transition-colors"
            >
              Subscribe for early access <ArrowRight size={16} aria-hidden="true" />
            </a>
          )}
        </div>
      )}

      {step === 'form' && (
        <EarlyAccessForm
          intro={
            notInvited
              ? "TAMAN is invite-only, so this Google account can't sign in yet. Request an invitation and we'll email you when your spot opens."
              : "We'll email you an invitation when access is available."
          }
          onCancel={() => setStep('intro')}
          onDone={(email, already) => {
            setSentTo(email);
            setAlreadyListed(already);
            setStep('done');
          }}
        />
      )}

      {step === 'done' && (
        <div className="mt-3 flex flex-col gap-3">
          <p className="text-sm text-white/80 leading-relaxed [overflow-wrap:anywhere]">
            {alreadyListed
              ? `${sentTo} asked before, so there's nothing else to do. Your invitation will come by email.`
              : `We'll email ${sentTo} as soon as your invitation is ready.`}
          </p>
          <ol className="flex flex-col gap-2 text-sm text-white/80">
            {[
              'We review your request',
              'Your invitation arrives by email',
              'Open it and set a password or use Google',
            ].map((text, i) => (
              <li key={text} className="flex items-center gap-2.5">
                <span className="w-5 h-5 shrink-0 rounded-full bg-green01/15 text-green01 text-[11px] font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                {text}
              </li>
            ))}
          </ol>
          <p className="flex items-center gap-2 text-xs text-white/60">
            <CheckCircle2 size={13} className="text-green01 shrink-0" aria-hidden="true" />
            Already invited? Use the link in your invitation email.
          </p>
        </div>
      )}
    </section>
  );
};

interface EarlyAccessFormProps {
  intro: string;
  onCancel: () => void;
  onDone: (email: string, already: boolean) => void;
}

const EarlyAccessForm = ({ intro, onCancel, onDone }: EarlyAccessFormProps) => {
  const { containerRef, execute } = useInvisibleRecaptcha(captchaSiteKey());
  const [email, setEmail] = useState('');
  const [profession, setProfession] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; profession?: string; privacy?: string; submit?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!EMAIL_RE.test(email.trim())) next.email = 'Enter a valid work email';
    if (!profession) next.profession = 'Choose what describes you best';
    if (!privacy) next.privacy = 'Accept the Privacy Policy to continue';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    const captchaToken = await execute().catch(() => null);
    if (!captchaToken) {
      setErrors({ submit: "We couldn't verify your request. Reload the page and try again." });
      setSubmitting(false);
      return;
    }
    try {
      const result = await joinWaitlist({
        email: email.trim(),
        profession,
        subscribedToNewsletter: newsletter,
        captchaToken,
      });
      onDone(email.trim(), result === 'already');
    } catch {
      setErrors({ submit: "Couldn't send your request. Try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-2 flex flex-col gap-3">
      <p className="text-sm text-white/80 leading-relaxed">{intro}</p>
      <AuthInput
        label="Work email"
        type="email"
        value={email}
        onChange={(v) => {
          setEmail(v);
          setErrors((p) => ({ ...p, email: undefined }));
        }}
        error={errors.email}
        placeholder="you@company.com"
        autoComplete="email"
        autoFocus
        name="waitlist-email"
      />

      <div className="w-full flex flex-col">
        <label htmlFor="waitlist-profession" className="block text-xs text-white/70 mb-1">
          What describes you best
        </label>
        {/* The native arrow sits flush against the border and differs per browser, so it is replaced with our own. */}
        <div className="relative">
          <select
            id="waitlist-profession"
            value={profession}
            onChange={(e) => {
              setProfession(e.target.value);
              setErrors((p) => ({ ...p, profession: undefined }));
            }}
            className={`w-full appearance-none bg-neutral-900 border text-base rounded-lg pl-3 pr-10 py-2 outline-none transition-colors ${
              profession ? 'text-white' : 'text-white/30'
            } ${errors.profession ? 'border-[#FF8A00]' : 'border-neutral-700 focus:border-green01'}`}
          >
            <option value="" disabled>
              Select an option
            </option>
            {PROFESSIONS.map((p) => (
              <option key={p.value} value={p.value} className="text-white">
                {p.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/50"
            aria-hidden="true"
          />
        </div>
        {errors.profession && <p className="text-xs mt-1 ml-0.5 text-[#FF8A00]">{errors.profession}</p>}
      </div>

      <label className="flex items-start gap-2 text-xs text-white/80 cursor-pointer leading-relaxed">
        <input
          type="checkbox"
          id="waitlist-privacy"
          checked={privacy}
          onChange={(e) => {
            setPrivacy(e.target.checked);
            setErrors((p) => ({ ...p, privacy: undefined }));
          }}
          className="w-3.5 h-3.5 mt-0.5 accent-green01 shrink-0"
        />
        <span>
          I accept the{' '}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green01 hover:underline"
          >
            Privacy Policy
          </a>
        </span>
      </label>
      {errors.privacy && <p className="-mt-2 text-xs ml-0.5 text-[#FF8A00]">{errors.privacy}</p>}

      <label className="flex items-start gap-2 text-xs text-white/60 cursor-pointer leading-relaxed">
        <input
          type="checkbox"
          id="waitlist-newsletter"
          checked={newsletter}
          onChange={(e) => setNewsletter(e.target.checked)}
          className="w-3.5 h-3.5 mt-0.5 accent-green01 shrink-0"
        />
        Send me product news and launch updates (optional)
      </label>

      {errors.submit && <p className="text-xs text-[#FF8A00]">{errors.submit}</p>}

      <AuthButton type="submit" loading={submitting}>
        Request invitation <ArrowRight size={16} aria-hidden="true" />
      </AuthButton>
      <button
        type="button"
        onClick={onCancel}
        className="self-center -my-1 px-3 py-2 text-xs text-white/60 hover:text-white"
      >
        Cancel
      </button>

      {/* On body so reCAPTCHA's fixed badge sits in the screen corner; inside the auth card it would widen the layout. */}
      {createPortal(<div ref={containerRef} />, document.body)}
    </form>
  );
};
